// src/components/PurchaseFlow/Steps/DataEntry/Sections/AddressContact.js
import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { faCheckCircle, faExclamationTriangle, faHouse, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from '../../../../../services/api';
import Swal from 'sweetalert2';
import { useSuggestedData } from '../../../../../context/SuggestedDataContext';
import './AddressContact.css';
import { Modal } from 'react-bootstrap';
import { DataSourceBadge, EditButton } from '../../../../Common';

const provinces = [{ "id": 2, "name": "Buenos Aires" }, { "id": 3, "name": "Capital Federal" }, { "id": 10, "name": "Catamarca" }, { "id": 8, "name": "Chaco" }, { "id": 19, "name": "Chubut" }, { "id": 22, "name": "Cordoba" }, { "id": 21, "name": "Corrientes" }, { "id": 5, "name": "Entre Rios" }, { "id": 14, "name": "Formosa" }, { "id": 23, "name": "Jujuy" }, { "id": 11, "name": "La Pampa" }, { "id": 6, "name": "La Rioja" }, { "id": 12, "name": "Mendoza" }, { "id": 13, "name": "Misiones" }, { "id": 15, "name": "Neuquen" }, { "id": 16, "name": "Rio Negro" }, { "id": 1, "name": "Salta" }, { "id": 9, "name": "San Juan" }, { "id": 4, "name": "San Luis" }, { "id": 24, "name": "Santa Cruz" }, { "id": 17, "name": "Santa Fe" }, { "id": 7, "name": "Santiago del Estero" }, { "id": 20, "name": "Tierra del Fuego" }, { "id": 18, "name": "Tucuman" }];

// Configuración para selección automática de datos de bureaus
const PRIORITIZED_DATA_SOURCES = ['siisa']; // Lista de sources priorizados, ordenados por prioridad
const RENEWER_CLIENT_TYPE = 'RENEWER';

const AddressContact = forwardRef((props, ref) => {
  const {
    formData,
    handleChange,
    activeSection,
    sectionName,
    setSectionValidity,
    sectionValidity,
    handleSectionClick,
    isLoadingSuggestions,
    displayMode = 'accordion',
    clientData
  } = props;

  const isContinuousMode = displayMode === 'continuous';

  const { suggestedData, selectedData, setSelectedData, setSuggestedData } = useSuggestedData();
  const [showNewForm, setShowNewForm] = useState(false);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [showNewAddressModal, setShowNewAddressModal] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editingAddressData, setEditingAddressData] = useState(null);

  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [localities, setLocalities] = useState([]);
  const [isLoadingLocalities, setIsLoadingLocalities] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Mover la definición de suggestions aquí, antes del useEffect
  const suggestions = suggestedData.address || [];

  // Función para auto-seleccionar datos del bureau priorizado para clientes renovadores
  const autoSelectPrioritizedData = () => {
    // Solo aplicar para clientes renovadores
    if (clientData?.client_type?.code !== RENEWER_CLIENT_TYPE) {
      return;
    }

    // Solo aplicar si no hay nada seleccionado aún
    if (selectedData.address) {
      return;
    }

    // Filtrar sugerencias que provienen de sources priorizados
    const prioritizedSuggestions = suggestions
      .filter(suggestion => {
        const sourceNormalized = suggestion.source?.toLowerCase();
        return PRIORITIZED_DATA_SOURCES.some(source => 
          source.toLowerCase() === sourceNormalized
        );
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Más reciente primero

    // Tomar el más reciente entre todos los sources priorizados
    const mostRecentPrioritized = prioritizedSuggestions[0];

    if (mostRecentPrioritized) {
      // Validar que la dirección tenga datos completos antes de auto-seleccionar
      const hasLocality = mostRecentPrioritized.locality && 
                          mostRecentPrioritized.locality !== null && 
                          mostRecentPrioritized.locality !== '';
      const hasProvince = mostRecentPrioritized.province && 
                          mostRecentPrioritized.province !== null && 
                          mostRecentPrioritized.province !== '';
      
      // Solo auto-seleccionar si tiene datos completos
      // Si está incompleto, simplemente no se selecciona.
      // La validación y alerta se mostrará solo si el usuario intenta seleccionarla manualmente
      if (hasLocality && hasProvince) {
        handleUseExisting(mostRecentPrioritized);
      }
    }
  };

  // Agregar useEffect para mantener la validación cuando cambia la sección activa
  useEffect(() => {
    if (activeSection === sectionName && (selectedData.address || touchedFields.address)) {
      validateSection();
    }
  }, [activeSection, sectionName, selectedData.address]);

  // Agregar useEffect para mantener la validación cuando cambia selectedData
  useEffect(() => {
    if (activeSection === sectionName && (selectedData.address || touchedFields.address)) {
      validateSection();
    }
  }, [selectedData.address, activeSection, sectionName]);

  const requiredFields = [
    'address',
    'province',
    'locality',
    'postalCode'
  ];

  useEffect(() => {
    const allFieldsTouched = requiredFields.every(field => touchedFields[field]);
    if (allFieldsTouched) {
      validateSection();
    }
  }, [touchedFields]);

  useEffect(() => {
    if (!formData.province) {
      setLocalities([]);
    }
  }, [formData.province]);

  useEffect(() => {
    if (activeSection === sectionName && suggestions !== undefined && suggestions !== null) {
      setShowNewForm(suggestions.length === 0);
    }
  }, [activeSection, sectionName, suggestions]);

  // UseEffect para auto-seleccionar datos del bureau priorizado
  useEffect(() => {
    if (suggestions.length > 0 && clientData) {
      autoSelectPrioritizedData();
    }
  }, [suggestions, clientData, selectedData.address]);

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'address':
        if (!value) {
          error = 'El domicilio es requerido';
        }
        break;
      case 'province':
        if (!value) {
          error = 'La provincia es requerida';
        }
        break;
      case 'locality':
        if (!value) {
          error = 'La localidad es requerida';
        }
        break;
      case 'postalCode':
        if (!value) {
          error = 'El código postal es requerido';
        }
        break;
      default:
        break;
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
    return error;
  };

  const validateSection = () => {
    // Si hay una dirección seleccionada, verificar que tenga datos completos
    if (selectedData.address) {
      // Buscar la dirección seleccionada en las sugerencias
      const selectedAddress = suggestions.find(s => s.id === selectedData.address);
      
      if (selectedAddress) {
        const hasLocality = selectedAddress.locality && selectedAddress.locality !== null && selectedAddress.locality !== '';
        const hasProvince = selectedAddress.province && selectedAddress.province !== null && selectedAddress.province !== '';
        
        if (!hasLocality || !hasProvince) {
          // La dirección seleccionada no tiene datos completos
          Swal.fire({
            icon: 'error',
            title: 'Información incompleta',
            html: `
              <p>La dirección seleccionada no tiene información completa:</p>
              <ul style="text-align: left; padding-left: 40px;">
                ${!hasProvince ? '<li>Falta la provincia</li>' : ''}
                ${!hasLocality ? '<li>Falta la localidad</li>' : ''}
              </ul>
              <p>Por favor, edite la dirección para completar esta información.</p>
            `,
            confirmButtonText: 'Entendido'
          });
          setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
          return false;
        }
      }
      
      setSectionValidity((prev) => ({ ...prev, [sectionName]: true }));
      return true;
    }

    // Si no hay dirección seleccionada, validar los campos
    const fieldsToValidate = ['address', 'province', 'locality', 'postalCode'];
    let newErrors = {};
    fieldsToValidate.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    setSectionValidity((prev) => ({ ...prev, [sectionName]: isValid }));
    return isValid;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    handleChange(e);
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name, value);
  };

  const handleProvinceChange = async (e) => {
    const { name, value } = e.target;
    handleFieldChange(e);

    // Resetear la localidad y su error cuando cambia la provincia
    handleFieldChange({
      target: {
        name: 'locality',
        value: ''
      }
    });
    setErrors(prev => ({
      ...prev,
      locality: ''
    }));

    if (value) {
      const cachedLocalities = getLocalitiesFromCache(value);
      if (cachedLocalities) {
        setLocalities(cachedLocalities);
      } else {
        setIsLoadingLocalities(true);
        try {
          const response = await api.get(`/provinces/${value}/localities`);
          setLocalities(response.data);
          saveLocalitiesToCache(value, response.data);
        } catch (error) {
          console.error('Error fetching localities:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudieron cargar las localidades. Por favor, intente nuevamente.',
          });
        } finally {
          setIsLoadingLocalities(false);
        }
      }
    } else {
      setLocalities([]);
    }
  };

  const handleLocalityChange = (e) => {
    const { name, value } = e.target;
    handleFieldChange(e);
    const selectedLocality = localities.find((loc) => loc.id === Number(value));
    if (selectedLocality) {
      handleFieldChange({
        target: { name: 'postalCode', value: selectedLocality.postal_code },
      });
      validateField('postalCode', selectedLocality.postal_code);
    }
  };

  const getLocalitiesFromCache = (provinceId) => {
    const cachedData = localStorage.getItem(`localities_${provinceId}`);
    return cachedData ? JSON.parse(cachedData) : null;
  };

  const saveLocalitiesToCache = (provinceId, localities) => {
    localStorage.setItem(`localities_${provinceId}`, JSON.stringify(localities));
  };

  // Helper function para buscar localidad por ID o nombre
  const findLocalityByIdOrName = (localityValue, localities) => {
    if (!localityValue || !localities.length) return null;
    
    // Primero intentar por ID directo
    if (!isNaN(localityValue)) {
      return localities.find(loc => loc.id === Number(localityValue));
    }
    
    // Si es un string, buscar por nombre
    if (typeof localityValue === 'string') {
      return localities.find(loc => 
        loc.name && loc.name.toLowerCase() === localityValue.toLowerCase()
      );
    }
    
    return null;
  };

  const handleUseExisting = (suggestion) => {
    // Validar que la dirección tenga datos completos de localidad y provincia
    const hasLocality = suggestion.locality && suggestion.locality !== null && suggestion.locality !== '';
    const hasProvince = suggestion.province && suggestion.province !== null && suggestion.province !== '';
    
    if (!hasLocality || !hasProvince) {
      Swal.fire({
        icon: 'warning',
        title: 'Información incompleta',
        html: `
          <p>Esta dirección no tiene información completa:</p>
          <ul style="text-align: left; padding-left: 40px;">
            ${!hasProvince ? '<li>Falta la provincia</li>' : ''}
            ${!hasLocality ? '<li>Falta la localidad</li>' : ''}
          </ul>
          <p>¿Desea completar esta información ahora?</p>
        `,
        showCancelButton: true,
        confirmButtonText: 'Sí, editar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#6c757d',
      }).then((result) => {
        if (result.isConfirmed) {
          // Abrir el modal de edición con esta dirección
          handleEditAddress(suggestion);
        }
      });
      return;
    }
    
    setSelectedData(prev => ({
      ...prev,
      address: suggestion.id
    }));
    // Update form data with the selected address details
    handleChange({ target: { name: 'address', value: suggestion.address } });
    handleChange({ target: { name: 'province', value: suggestion.province } });
    handleChange({ target: { name: 'locality', value: suggestion.locality } });
    handleChange({ target: { name: 'postalCode', value: suggestion.postal_code } });
    setSectionValidity(prev => ({ ...prev, [sectionName]: true }));
  };

  const handleUseNewForm = () => {
    setShowNewAddressModal(true);
    setIsEditingAddress(false);
    setEditingAddressData(null);
    setSelectedData(prev => ({
      ...prev,
      address: null
    }));
    // Limpiar los campos del formulario
    handleChange({ target: { name: 'address', value: '' } });
    handleChange({ target: { name: 'province', value: '' } });
    handleChange({ target: { name: 'locality', value: '' } });
    handleChange({ target: { name: 'postalCode', value: '' } });
    setErrors({});
    setTouchedFields({});
    setLocalities([]);
    setFormSubmitted(false);
  };

  const handleEditAddress = async (suggestion) => {
    setIsEditingAddress(true);
    setEditingAddressData(suggestion);
    setShowNewAddressModal(true);
    
    // Encontrar el ID de la provincia basado en el nombre o usar el valor directamente si ya es un ID
    let provinceId = suggestion.province;
    if (typeof suggestion.province === 'string' && isNaN(suggestion.province)) {
      // Si es un string (nombre), buscar el ID
      const foundProvince = provinces.find(p => p.name === suggestion.province);
      provinceId = foundProvince ? foundProvince.id : suggestion.province;
    }
    
    // Precargar los datos básicos del formulario
    handleChange({ target: { name: 'address', value: suggestion.address } });
    handleChange({ target: { name: 'province', value: provinceId } });
    handleChange({ target: { name: 'postalCode', value: suggestion.postal_code } });
    
    // Cargar las localidades de la provincia si es necesario
    if (provinceId) {
      const cachedLocalities = getLocalitiesFromCache(provinceId);
      if (cachedLocalities && cachedLocalities.length > 0) {
        setLocalities(cachedLocalities);
      } else {
        setIsLoadingLocalities(true);
        try {
          const response = await api.get(`/provinces/${provinceId}/localities`);
          const loadedLocalities = response.data;
          setLocalities(loadedLocalities);
          saveLocalitiesToCache(provinceId, loadedLocalities);
        } catch (error) {
          console.error('Error fetching localities:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudieron cargar las localidades. Por favor, intente nuevamente.',
          });
        } finally {
          setIsLoadingLocalities(false);
        }
      }
    }
    
    setErrors({});
    setTouchedFields({});
    setFormSubmitted(false);
  };

  const handleCloseModal = () => {
    setShowNewAddressModal(false);
    setIsEditingAddress(false);
    setEditingAddressData(null);
    // Resetear todos los campos de una vez
    handleChange({
      target: { name: 'address', value: '' }
    });
    handleChange({
      target: { name: 'province', value: '' }
    });
    handleChange({
      target: { name: 'locality', value: '' }
    });
    handleChange({
      target: { name: 'postalCode', value: '' }
    });
    setErrors({});
    setTouchedFields({});
    setLocalities([]);
    setFormSubmitted(false);
  };

  const handleSaveNewAddress = () => {
    setFormSubmitted(true);
    const isValid = validateSection();
    if (!isValid) return;

    // Check for duplicates solo si no estamos editando
    if (!isEditingAddress) {
      const isDuplicate = checkForDuplicates();
      if (isDuplicate) {
        Swal.fire({
          icon: 'error',
          title: 'Domicilio duplicado',
          text: 'Este domicilio ya existe en el sistema.',
          confirmButtonText: 'Entendido'
        });
        return;
      }
    }

    // Obtener los nombres correctos de provincia y localidad
    const selectedProvince = provinces.find(p => p.id === Number(formData.province));
    const selectedLocality = localities.find(l => l.id === Number(formData.locality));

    // Crear el objeto de la dirección (nueva o editada)
    const addressData = {
      id: isEditingAddress ? `edited_${editingAddressData.id}_${Date.now()}` : `new_${Date.now()}`,
      address: formData.address,
      province: selectedProvince?.id || '',
      province_label: selectedProvince?.name || '',
      locality: selectedLocality?.id || '',
      locality_label: selectedLocality?.name || '',
      postal_code: formData.postalCode,
      created_at: new Date().toISOString(),
      // Marcar si es una edición para el badge
      isEdited: isEditingAddress,
      originalId: isEditingAddress ? editingAddressData.id : null
    };

    // Si estamos editando, marcar la dirección original como reemplazada
    let updatedSuggestions;
    if (isEditingAddress) {
      updatedSuggestions = suggestions.map(suggestion => 
        suggestion.id === editingAddressData.id 
          ? { ...suggestion, replacedBy: addressData.id }
          : suggestion
      );
      // Agregar la nueva dirección editada
      updatedSuggestions = [addressData, ...updatedSuggestions];
    } else {
      // Para nuevas direcciones, simplemente agregar al inicio
      updatedSuggestions = [addressData, ...suggestions];
    }

    // Seleccionar la dirección por defecto
    setSelectedData(prev => ({
      ...prev,
      address: addressData.id
    }));

    // Actualizar el contexto con las nuevas sugerencias
    setSuggestedData(prev => ({
      ...prev,
      address: updatedSuggestions
    }));

    // IMPORTANTE: Actualizar el formData con los valores seleccionados
    // Esto asegura que los datos estén disponibles cuando se envíe el formulario
    handleChange({
      target: {
        name: 'address',
        value: formData.address
      }
    });
    handleChange({
      target: {
        name: 'province',
        value: selectedProvince?.id || ''
      }
    });
    handleChange({
      target: {
        name: 'locality',
        value: selectedLocality?.id || ''
      }
    });
    handleChange({
      target: {
        name: 'postalCode',
        value: formData.postalCode
      }
    });

    // Cerrar el modal
    setShowNewAddressModal(false);
    setIsEditingAddress(false);
    setEditingAddressData(null);

    // Limpiar solo los campos del modal
    setErrors({});
    setTouchedFields({});
    setLocalities([]);

    // Mostrar mensaje de éxito
    Swal.fire({
      icon: 'success',
      title: isEditingAddress ? 'Domicilio editado' : 'Domicilio agregado',
      text: isEditingAddress ? 'El domicilio ha sido editado correctamente.' : 'El nuevo domicilio ha sido agregado correctamente.',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const checkForDuplicates = () => {
    const normalizedAddress = String(formData.address).trim().toLowerCase();
    const normalizedProvince = provinces.find(p => p.id === Number(formData.province))?.name.trim().toLowerCase();
    const normalizedLocality = localities.find(l => l.id === Number(formData.locality))?.name.trim().toLowerCase();
    const normalizedPostalCode = String(formData.postalCode).trim().toLowerCase();

    return suggestions.some(suggestion => {
      const existingAddress = String(suggestion.address).trim().toLowerCase();
      const existingProvince = String(suggestion.province).trim().toLowerCase();
      const existingLocality = String(suggestion.locality).trim().toLowerCase();
      const existingPostalCode = String(suggestion.postal_code).trim().toLowerCase();

      const isMatch = existingAddress === normalizedAddress &&
        existingProvince === normalizedProvince &&
        existingLocality === normalizedLocality &&
        existingPostalCode === normalizedPostalCode;

      return isMatch;
    });
  };

  // Filtrar duplicados, sugerencias reemplazadas y ordenar por fecha (más reciente primero)
  const uniqueSuggestions = suggestions
    .filter((sugg, idx, arr) => arr.findIndex(s => s.id === sugg.id) === idx)
    .filter(sugg => !sugg.replacedBy) // Ocultar sugerencias que han sido reemplazadas
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Obtener las sugerencias a mostrar
  const displayedSuggestions = showAllSuggestions
    ? uniqueSuggestions
    : uniqueSuggestions.filter((sugg, index) =>
      index === 0 || // Siempre mostrar la más reciente
      sugg.id.toString().startsWith('new_') || // Mostrar las nuevas si hay ocultas
      sugg.id.toString().startsWith('edited_') // Mostrar las editadas
    );

  // useEffect para manejar la selección de localidad cuando se edita una dirección
  useEffect(() => {
    if (isEditingAddress && editingAddressData && localities.length > 0 && !formData.locality) {
      const localityToSelect = findLocalityByIdOrName(editingAddressData.locality, localities);
      
      if (localityToSelect) {
        handleChange({ target: { name: 'locality', value: localityToSelect.id } });
      }
    }
  }, [localities, isEditingAddress, editingAddressData, formData.locality]);

  useImperativeHandle(ref, () => ({
    validateSection,
  }));

  // Renderizado condicional basado en el modo de visualización
  if (isContinuousMode) {
    return (
      <div className="continuous-section mb-2">
        <div className="section-header mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="d-flex align-items-center mb-0 fw-light">
              <FontAwesomeIcon icon={faHouse} className="me-2" />
              Domicilio
              {sectionValidity[sectionName] === true && (
                <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
              )}
              {sectionValidity[sectionName] === false && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
              )}
            </h5>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={handleUseNewForm}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Nuevo Domicilio
            </button>
          </div>
        </div>
        <div className="section-content">
          {isLoadingSuggestions ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2 text-muted">Cargando direcciones sugeridas...</p>
            </div>
          ) : (
            <div className="suggested-data">
              <div className="mb-3">
                <h6 className="mb-0">Gestione sus domicilios</h6>
              </div>
              <div className="row g-1">
                {displayedSuggestions.map(suggestion => (
                  <div key={`${suggestion.id}-${suggestion.created_at}`} className="col-12">
                    <div className="card suggested-address-card mb-1 position-relative">
                      <div className="card-body">
                        <div className="form-check d-flex align-items-center">
                          <input
                            className="form-check-input mt-0"
                            type="radio"
                            name="addressSelection"
                            id={`address-${suggestion.id}`}
                            onChange={() => handleUseExisting(suggestion)}
                            checked={selectedData.address === suggestion.id}
                            style={{ marginTop: 0, marginBottom: 0 }}
                          />
                          <label className="form-check-label w-100 ms-2" htmlFor={`address-${suggestion.id}`} style={{ cursor: 'pointer' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <p className="mb-1 fw-medium">{suggestion.address}</p>
                                <small className="text-muted">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ fontSize: '0.7rem' }} />
                                  {suggestion.locality_label || suggestion.locality}, {suggestion.province_label || suggestion.province}
                                </small>
                              </div>
                              <div className="d-flex flex-column align-items-end gap-1">
                                {suggestion.id.toString().startsWith('new_') && (
                                  <span className="badge bg-primary">Nuevo</span>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                        <DataSourceBadge dataItem={suggestion} options={{ position: 'top-right' }} />
                        {/* Botón de edición - mostrar para direcciones originales y editadas, pero no para nuevas */}
                        {(!suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')) && (
                          <EditButton 
                            onEdit={() => handleEditAddress(suggestion)} 
                            options={{ position: 'bottom-right', variant: 'muted' }} 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="col-12 mt-3 d-flex justify-content-between align-items-center">
                  <div>
                    {!showAllSuggestions && uniqueSuggestions.length > displayedSuggestions.length && (
                      <button
                        className="btn btn-link text-decoration-none"
                        onClick={() => setShowAllSuggestions(true)}
                      >
                        Ver {uniqueSuggestions.length - displayedSuggestions.length} más
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal para nueva dirección */}
        <Modal show={showNewAddressModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEditingAddress ? 'Editar Domicilio' : 'Nuevo Domicilio'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Domicilio<sup>*</sup></label>
                <input
                  type="text"
                  className={`form-control ${(errors.address && (formSubmitted || touchedFields.address)) ? 'is-invalid' : ''}`}
                  name="address"
                  value={formData.address || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                  required
                />
                {errors.address && <div className="invalid-feedback">{errors.address}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Provincia<sup>*</sup></label>
                <select
                  className={`form-control ${(errors.province && (formSubmitted || touchedFields.province)) ? 'is-invalid' : ''}`}
                  name="province"
                  value={formData.province || ''}
                  onChange={handleProvinceChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                  required
                >
                  <option value="" disabled>Seleccione una provincia</option>
                  {provinces.map((prov) => (
                    <option key={prov.id} value={prov.id}>{prov.name}</option>
                  ))}
                </select>
                {errors.province && <div className="invalid-feedback">{errors.province}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Localidad<sup>*</sup></label>
                <div className="position-relative">
                  <select
                    className={`form-control ${(errors.locality && (formSubmitted || touchedFields.locality)) ? 'is-invalid' : ''}`}
                    name="locality"
                    value={formData.locality || ''}
                    onChange={handleLocalityChange}
                    onBlur={handleFieldBlur}
                    autoComplete="off"
                    required
                    disabled={!formData.province || isLoadingLocalities}
                  >
                    <option value="" disabled>
                      {!formData.province
                        ? 'Primero seleccione una provincia'
                        : isLoadingLocalities
                          ? 'Cargando localidades...'
                          : 'Seleccione una localidad'
                      }
                    </option>
                    {localities.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                  {isLoadingLocalities && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                    </div>
                  )}
                  {errors.locality && !isLoadingLocalities && (
                    <div className="invalid-feedback">{errors.locality}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Código Postal<sup>*</sup></label>
                <input
                  type="text"
                  className={`form-control ${(errors.postalCode && (formSubmitted || touchedFields.postalCode)) ? 'is-invalid' : ''}`}
                  name="postalCode"
                  value={formData.postalCode || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                  required
                />
                {errors.postalCode && <div className="invalid-feedback">{errors.postalCode}</div>}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-between w-100">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSaveNewAddress}>
                {isEditingAddress ? 'Guardar Cambios' : 'Guardar Domicilio'}
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  // Modo accordion (original)
  return (
    <div className="accordion-item">
      <h2 className="accordion-header" id={`heading${sectionName}`}>
        <button
          className={`accordion-button ${activeSection !== sectionName ? 'collapsed' : ''}`}
          type="button"
          onClick={() => handleSectionClick(sectionName)}
        >
          <FontAwesomeIcon icon={faHouse} className="me-2" />
          Domicilio{' '}
          {sectionValidity[sectionName] === true && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          )}
          {sectionValidity[sectionName] === false && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
          )}
        </button>
      </h2>
      <div
        className={`accordion-collapse collapse ${activeSection === sectionName ? 'show' : ''}`}
        aria-labelledby={`heading${sectionName}`}
      >
        <div className="accordion-body">
          {isLoadingSuggestions ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2 text-muted">Cargando direcciones sugeridas...</p>
            </div>
          ) : (
            <div className="suggested-data">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Gestione sus domicilios</h6>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleUseNewForm}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Nuevo Domicilio
                </button>
              </div>
              <div className="row g-1">
                {displayedSuggestions.map(suggestion => (
                  <div key={`${suggestion.id}-${suggestion.created_at}`} className="col-12">
                    <div className="card suggested-address-card mb-1 position-relative">
                      <div className="card-body">
                        <div className="form-check d-flex align-items-center">
                          <input
                            className="form-check-input mt-0"
                            type="radio"
                            name="addressSelection"
                            id={`address-${suggestion.id}`}
                            onChange={() => handleUseExisting(suggestion)}
                            checked={selectedData.address === suggestion.id}
                            style={{ marginTop: 0, marginBottom: 0 }}
                          />
                          <label className="form-check-label w-100 ms-2" htmlFor={`address-${suggestion.id}`} style={{ cursor: 'pointer' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <p className="mb-1 fw-medium">{suggestion.address}</p>
                                <small className="text-muted">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ fontSize: '0.7rem' }} />
                                  {suggestion.locality_label || suggestion.locality}, {suggestion.province_label || suggestion.province}
                                </small>
                              </div>
                              <div className="d-flex flex-column align-items-end gap-1">
                                {suggestion.id.toString().startsWith('new_') && (
                                  <span className="badge bg-primary">Nuevo</span>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                        {/* Badge de origen de datos */}
                        <DataSourceBadge dataItem={suggestion} options={{ position: 'top-right' }} />
                        {/* Botón de edición - mostrar para direcciones originales y editadas, pero no para nuevas */}
                        {(!suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')) && (
                          <EditButton 
                            onEdit={() => handleEditAddress(suggestion)} 
                            options={{ position: 'bottom-right', variant: 'muted' }} 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="col-12 mt-3 d-flex justify-content-between align-items-center">
                  <div>
                    {!showAllSuggestions && uniqueSuggestions.length > displayedSuggestions.length && (
                      <button
                        className="btn btn-link text-decoration-none"
                        onClick={() => setShowAllSuggestions(true)}
                      >
                        Ver {uniqueSuggestions.length - displayedSuggestions.length} más
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal para nueva dirección */}
      <Modal show={showNewAddressModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditingAddress ? 'Editar Domicilio' : 'Nuevo Domicilio'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Domicilio<sup>*</sup></label>
              <input
                type="text"
                className={`form-control ${(errors.address && (formSubmitted || touchedFields.address)) ? 'is-invalid' : ''}`}
                name="address"
                value={formData.address || ''}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
                required
              />
              {errors.address && <div className="invalid-feedback">{errors.address}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Provincia<sup>*</sup></label>
              <select
                className={`form-control ${(errors.province && (formSubmitted || touchedFields.province)) ? 'is-invalid' : ''}`}
                name="province"
                value={formData.province || ''}
                onChange={handleProvinceChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
                required
              >
                <option value="" disabled>Seleccione una provincia</option>
                {provinces.map((prov) => (
                  <option key={prov.id} value={prov.id}>{prov.name}</option>
                ))}
              </select>
              {errors.province && <div className="invalid-feedback">{errors.province}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Localidad<sup>*</sup></label>
              <div className="position-relative">
                <select
                  className={`form-control ${(errors.locality && (formSubmitted || touchedFields.locality)) ? 'is-invalid' : ''}`}
                  name="locality"
                  value={formData.locality || ''}
                  onChange={handleLocalityChange}
                  onBlur={handleFieldBlur}
                  autoComplete="off"
                  required
                  disabled={!formData.province || isLoadingLocalities}
                >
                  <option value="" disabled>
                    {!formData.province
                      ? 'Primero seleccione una provincia'
                      : isLoadingLocalities
                        ? 'Cargando localidades...'
                        : 'Seleccione una localidad'
                    }
                  </option>
                  {localities.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
                {isLoadingLocalities && (
                  <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                  </div>
                )}
                {errors.locality && !isLoadingLocalities && (
                  <div className="invalid-feedback">{errors.locality}</div>
                )}
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Código Postal<sup>*</sup></label>
              <input
                type="text"
                className={`form-control ${(errors.postalCode && (formSubmitted || touchedFields.postalCode)) ? 'is-invalid' : ''}`}
                name="postalCode"
                value={formData.postalCode || ''}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
                required
              />
              {errors.postalCode && <div className="invalid-feedback">{errors.postalCode}</div>}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <button className="btn btn-secondary" onClick={handleCloseModal}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleSaveNewAddress}>
              {isEditingAddress ? 'Guardar Cambios' : 'Guardar Domicilio'}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default AddressContact;